import { i18n } from '@lingui/core';
import { isWithData, isWithError, isWithReason } from '@orionprotocol/utils';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { BaseContract } from 'ethers';
import { sentryLogger } from '@orionprotocol/common';
import { useToast } from '@orionprotocol/ui';

import { Contracts } from '../types';
import { getExchangeContractAddress } from '../redux/selectors';
import { useProvider, useProviderOrSigner } from '../hooks';
import { useVeOrnVotingContract } from '../../entities/governance';

import { useExchangeContract } from './useExchangeContract';

export function useProcessContractError(): (
  error: unknown,
  contract?: Contracts,
) => void {
  const exchangeContractAddress = useSelector(getExchangeContractAddress);
  const provider = useProvider();
  const { signer } = useProviderOrSigner();
  const veOrnVotingContract = useVeOrnVotingContract(signer);
  const exchangeContract = useExchangeContract(
    exchangeContractAddress,
    provider,
  );

  const { showToast } = useToast();

  const processContractError = useCallback(
    (error: unknown, contract?: Contracts): void => {
      sentryLogger.error(error);
      let err = error;

      if (isWithError(error)) {
        err = error.error;
      }

      if (isWithReason(err)) {
        showToast({
          text: err.reason,
          iconType: 'error',
        });

        return;
      }

      let errorData;

      if (typeof err === 'object' && err !== null && 'data' in err) {
        if (typeof err.data === 'string') {
          errorData = err.data;
        } else if (isWithData(err) && typeof err.data.data === 'string') {
          errorData = err.data.data;
        }
      }

      let currentContract: BaseContract | undefined;

      switch (contract) {
        case Contracts.Exchange:
          if (exchangeContractAddress !== undefined) {
            currentContract = exchangeContract;
          }
          break;

        case Contracts.VeOrnVoting:
          currentContract = veOrnVotingContract;
          break;

        default:
          break;
      }

      if (
        currentContract !== undefined &&
        errorData !== undefined &&
        provider !== undefined
      ) {
        const decodedError = currentContract.interface.parseError(errorData);

        if (decodedError === null) {
          showToast({
            text: errorData,
            iconType: 'error',
          });
        } else {
          showToast({
            text: decodedError.name,
            iconType: 'error',
          });
        }
      } else {
        showToast({
          text:
            typeof err === 'string'
              ? err
              : i18n._(
                  'Contract interaction error occurred. Please reload the page and try again.',
                ),
          iconType: 'error',
        });
      }
    },
    [
      exchangeContract,
      exchangeContractAddress,
      provider,
      showToast,
      veOrnVotingContract,
    ],
  );

  return processContractError;
}
